import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { type ListSigningDto, type SignerResponseDto } from 'xosign-types';
import classNames from 'classnames';
import {
  API_URL_BASE,
  POLL_INTERVALL_SIGNERS,
  POLL_INTERVALL_STARTING,
} from '@/config';
import preloadChunks from '@/routes/preloadChunks.util';
import useApi from '@/hooks/useApi';
import triggerDownloadFlow from '@/utils/downloadFile';
import IcArrowRight from '@/assets/icons/ArrowRight.svg?react';
import IcCheck from '@/assets/icons/Check.svg?react';
import IcCross from '@/assets/icons/Cross.svg?react';
import IcExport from '@/assets/icons/Export.svg?react';
import IcTrash from '@/assets/icons/Trash.svg?react';
import styles from './ListItem.module.scss';

dayjs.extend(relativeTime);

type SpecialSigningDetails = {
  expirationDate: string | null;
  state: string;
  updatedAt: string;
  archivedAt: string | null;
};

type CombinedSigner = {
  id: string;
  name: string;
  state: string;
};

type PartySigner = CombinedSigner & {
  kind?: string;
  representativeOf?: string;
};

// The list card shows a company as one party: signed once every named
// representative has signed, pending otherwise.
const collapseParties = (rows: PartySigner[]): CombinedSigner[] => {
  const out: CombinedSigner[] = [];
  for (const row of rows) {
    if (row.representativeOf) {
      continue;
    }

    if (row.kind !== 'company') {
      out.push({ id: row.id, state: row.state, name: row.name });
      continue;
    }

    const reps = rows.filter((r) => r.representativeOf === row.id);
    const allSigned =
      reps.length > 0 && reps.every((r) => r.state === 'SIGNED');
    out.push({
      id: row.id,
      state: allSigned ? 'SIGNED' : 'PENDING',
      name: row.name,
    });
  }
  return out;
};

type BadgeKind =
  | 'draft'
  | 'preparing'
  | 'active'
  | 'done'
  | 'expired'
  | 'cancelled';

interface ListItemProps {
  signing: ListSigningDto;
  isLoading: boolean;
  isDeleting?: boolean;
  onDelete?: (signing: ListSigningDto) => void;
}

const ListItem = ({
  signing,
  isLoading,
  isDeleting = false,
  onDelete,
}: ListItemProps) => {
  const { t } = useTranslation(['common', 'signings']);

  const [loadSigners, { response: signersResponse, loading: signersLoading }] =
    useApi('signers.get');
  const [loadSigning] = useApi('signings.get');

  const [details, setDetails] = useState<SpecialSigningDetails>({
    expirationDate: signing.expirationDate,
    state: signing.state,
    updatedAt: signing.updatedAt,
    archivedAt: signing.archivedAt ?? null,
  });
  const [downloading, setDownloading] = useState(false);

  const signers: SignerResponseDto[] = signersResponse?.data ?? [];

  const handlePreloadAuditTrail = () => {
    void preloadChunks['signings.report.auditTrail']();
  };

  const handlePreloadDetail = () => {
    void preloadChunks['signings.report.summary']();
  };

  const handlePreloadSigning = () => {
    void preloadChunks['signings.workflow']();
  };

  const handleDeleteClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete?.(signing);
  };

  const handleDownloadDocument = async () => {
    if (downloading) {
      return;
    }

    // The list payload has no document ids — fetch the signing detail first.
    setDownloading(true);
    const result = await loadSigning({ workflowId: signing.id });
    setDownloading(false);

    const documents = result?.response?.data?.documents;
    if (!documents || documents.length === 0) {
      toast.error(t('signings:download.failed'));
      return;
    }

    const url =
      documents.length > 1
        ? `${API_URL_BASE}signings/${signing.id}/documents/download-all`
        : `${API_URL_BASE}signings/${signing.id}/documents/${documents[0].id}`;

    triggerDownloadFlow(url, localStorage.getItem('token'), {
      loading: t('signings:download.downloading'),
      failed: t('signings:download.failed'),
      success: t('signings:download.success'),
    });
  };

  useEffect(() => {
    if (details.state !== 'STARTING') {
      return;
    }

    let timeoutRef: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const poll = async () => {
      const result = await loadSigning({ workflowId: signing.id });
      if (cancelled) {
        return;
      }

      if (result?.response?.data) {
        if (result.response.data.state === 'STARTING') {
          timeoutRef = setTimeout(() => {
            void poll();
          }, POLL_INTERVALL_STARTING);
        } else {
          setDetails({
            expirationDate: result.response.data.expirationDate,
            state: result.response.data.state,
            updatedAt: result.response.data.updatedAt,
            archivedAt: result.response.data.archivedAt ?? null,
          });
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef);
    };
  }, [loadSigning, signing.id, details.state]);

  useEffect(() => {
    if (details.state !== 'SIGNING') {
      return;
    }

    const delay = signersResponse?.data ? POLL_INTERVALL_SIGNERS : 0;
    const timeoutRef = setTimeout(() => {
      void loadSigners({ workflowId: signing.id });
    }, delay);

    return () => {
      clearTimeout(timeoutRef);
    };
    // signersLoading added to set new data after reload
  }, [
    loadSigners,
    signersLoading,
    signersResponse?.data,
    signing.id,
    details.state,
  ]);

  const signersCombined: CombinedSigner[] = (() => {
    if (signers.length > 0) {
      return collapseParties(signers);
    }
    if (signing.signers.length > 0) {
      return collapseParties(
        signing.signers.map((signer) => ({ ...signer, state: 'PENDING' }))
      );
    }

    return [
      {
        id: 'tbd',
        state: 'PENDING',
        name: t('signings:notYetDefined'),
      },
    ];
  })();

  const badgeKind: BadgeKind = (() => {
    switch (details.state) {
      case 'DRAFT':
        return 'draft';
      case 'STARTING':
        return 'preparing';
      case 'IN_REVIEW':
      case 'SIGNING':
        return 'active';
      case 'EXPIRED':
        return 'expired';
      case 'CANCELLED':
        return 'cancelled';
      default:
        return 'done';
    }
  })();

  const badgeLabel = t(`signings:badge.${badgeKind}`);

  const metaLabel = (() => {
    switch (badgeKind) {
      case 'draft':
        return t('signings:meta.lastModified');
      case 'preparing':
      case 'active':
        return t('signings:meta.sent');
      case 'done':
        return t('signings:meta.completed');
      case 'expired':
        return t('signings:meta.expired');
      case 'cancelled':
        return t('signings:meta.cancelled');
      default:
        return '';
    }
  })();

  const metaDate = dayjs(
    badgeKind === 'expired' && details.expirationDate
      ? details.expirationDate
      : details.updatedAt
  ).format(t('common:dateTime.dateTimeDisplayShort'));

  const showAuditTrail =
    details.state !== 'DRAFT' && details.state !== 'STARTING';

  const isDone = details.state === 'COMPLETED';
  const documentArchived =
    !!details.archivedAt && !dayjs(details.archivedAt).isAfter(dayjs());
  const showDownload = isDone && !documentArchived;
  const availabilityHint =
    showDownload && details.archivedAt
      ? t('signings:download.availableFor', {
          relative: dayjs().to(details.archivedAt, true),
        })
      : null;

  const isDraft = details.state === 'DRAFT' || details.state === 'STARTING';
  const coverLinkTarget = isDraft
    ? `/signings/${signing.id}`
    : `/signings/${signing.id}/report/summary`;
  const auditTrailHref = `/signings/${signing.id}/report/auditTrail`;

  const signingIsActive =
    details.state === 'IN_REVIEW' ||
    details.state === 'STARTING' ||
    details.state === 'SIGNING';

  type SignerKind = 'signed' | 'declined' | 'waiting' | 'pending';

  const signerKind = (state: string): SignerKind => {
    if (state === 'SIGNED') {
      return 'signed';
    }
    if (state === 'EXPIRED' || state === 'REJECTED' || state === 'DECLINED') {
      return 'declined';
    }
    if (signingIsActive) {
      return 'waiting';
    }
    return 'pending';
  };

  const renderSignerDot = (kind: SignerKind): ReactNode => {
    if (kind === 'signed') {
      return <IcCheck className={styles.signerDotIcon} />;
    }
    if (kind === 'declined') {
      return <IcCross className={styles.signerDotIcon} />;
    }
    if (kind === 'waiting') {
      return (
        <svg
          className={styles.signerDotClock}
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="8"
            cy="8"
            r="5.5"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <path
            d="M8 5V8L10 9.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    return null;
  };

  const canDelete =
    (details.state === 'DRAFT' ||
      details.state === 'EXPIRED' ||
      details.state === 'CANCELLED') &&
    !!onDelete;

  return (
    <li
      className={classNames(styles.card, styles[`card-${badgeKind}`], {
        [styles.isLoading]: isLoading,
        [styles.isDeleting]: isDeleting,
      })}
    >
      <NavLink
        to={coverLinkTarget}
        className={styles.coverLink}
        onMouseEnter={isDraft ? handlePreloadSigning : handlePreloadDetail}
        onFocus={isDraft ? handlePreloadSigning : handlePreloadDetail}
        aria-label={t('common:button.viewAndEdit')}
      />

      <div className={styles.typeColumn}>
        <span className={styles.typeBadge}>
          {signing.signatureType === 'QUALIFIED' ? 'QES' : 'AES'}
        </span>
      </div>

      <div className={styles.contentColumn}>
        <h3 className={styles.title}>
          <span className={styles.titleText}>{signing.name}</span>
          <span
            className={classNames(
              styles.statusBadge,
              styles[`statusBadge-${badgeKind}`]
            )}
          >
            {badgeLabel}
          </span>
        </h3>
        <ol className={styles.signers}>
          {signersCombined.map((signer) => {
            const kind = signerKind(signer.state);
            return (
              <li
                key={signer.id}
                className={classNames(styles.signer, styles[`signer-${kind}`])}
              >
                <span className={styles.signerDot}>
                  {renderSignerDot(kind)}
                </span>
                <span className={styles.signerName}>{signer.name}</span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className={styles.metaColumn}>
        <div className={styles.metaLabel}>{metaLabel}</div>
        <div className={styles.metaValue}>{metaDate}</div>
        {showAuditTrail && (
          <div
            className={classNames(styles.actionsRow, {
              [styles.actionsRowSplit]: isDone,
            })}
          >
            {showDownload && (
              <button
                type="button"
                className={styles.downloadButton}
                onClick={() => void handleDownloadDocument()}
                disabled={downloading}
                title={availabilityHint ?? t('signings:download.title')}
              >
                <IcExport className={styles.downloadIcon} aria-hidden="true" />
                {t('signings:download.label')}
              </button>
            )}
            {isDone && documentArchived && (
              <span className={styles.archivedNote}>
                {t('signings:download.isArchived')}
              </span>
            )}
            <NavLink
              to={auditTrailHref}
              className={styles.auditTrailLink}
              onMouseEnter={handlePreloadAuditTrail}
              onFocus={handlePreloadAuditTrail}
            >
              {t('signings:auditTrail.title')}
              <IcArrowRight className={styles.auditTrailIcon} />
            </NavLink>
          </div>
        )}
      </div>

      {/* Always render the delete slot so rows align uniformly; only deletable
          rows expose an active button (others get a hidden placeholder). */}
      <div className={styles.deleteColumn}>
        {canDelete ? (
          <button
            type="button"
            className={styles.deleteButton}
            aria-label={t('signings:buttons.deleteAriaLabel')}
            onClick={handleDeleteClick}
            disabled={isDeleting}
          >
            <IcTrash className={styles.deleteIcon} aria-hidden="true" />
          </button>
        ) : (
          <span
            className={classNames(
              styles.deleteButton,
              styles.deletePlaceholder
            )}
            aria-hidden="true"
          />
        )}
      </div>
    </li>
  );
};

export default ListItem;
